import { useEffect, useMemo, useRef, useState } from "react";
import {
  PRESSKIT_LIMITS,
  deletePresskitAsset,
  fetchAdminPresskitAssets,
  formatFileSize,
  reorderPresskitAssets,
  replacePresskitAsset,
  updatePresskitAsset,
  uploadPresskitAsset,
} from "../../api/presskit";
import PresskitMediaCard from "./PresskitMediaCard";
import { presskitAcceptByType } from "./presskitMediaConfig";

export default function PresskitMediaManager() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [draggedId, setDraggedId] = useState("");
  const measuredIds = useRef(new Set());

  const groupedAssets = useMemo(() => ({
    image: assets.filter((asset) => asset.mediaType === "image").sort((a, b) => a.displayOrder - b.displayOrder),
    video: assets.filter((asset) => asset.mediaType === "video").sort((a, b) => a.displayOrder - b.displayOrder),
  }), [assets]);

  useEffect(() => {
    fetchAdminPresskitAssets()
      .then(setAssets)
      .catch((error) => setStatus({ type: "error", message: error.message }))
      .finally(() => setLoading(false));
  }, []);

  const replaceAssetInState = (updated) => {
    setAssets((current) => current.map((asset) => asset.id === updated.id ? updated : asset));
  };

  async function handleUpload(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const file = data.get("media");
    const altText = data.get("description");
    setBusy("upload");
    setUploadProgress(0);
    setStatus({ type: "", message: "" });
    try {
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      const displayOrder = Math.max(0, ...groupedAssets[mediaType].map((asset) => asset.displayOrder)) + 10;
      const created = await uploadPresskitAsset({ file, altText, displayOrder, onProgress: setUploadProgress });
      setAssets((current) => [...current, created]);
      form.reset();
      setStatus({ type: "success", message: `${created.mediaType === "image" ? "Photo" : "Video"} uploaded and published.` });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
      setUploadProgress(null);
    }
  }

  async function handleDescription(asset, altText) {
    setBusy(asset.id);
    setStatus({ type: "", message: "" });
    try {
      const updated = await updatePresskitAsset(asset.id, { altText: altText.trim() });
      replaceAssetInState(updated);
      setStatus({ type: "success", message: "Description saved." });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
    }
  }

  async function handleVisibility(asset) {
    setBusy(asset.id);
    setStatus({ type: "", message: "" });
    try {
      const updated = await updatePresskitAsset(asset.id, { isVisible: !asset.isVisible });
      replaceAssetInState(updated);
      setStatus({ type: "success", message: updated.isVisible ? "Media published." : "Media hidden from the public Press Kit." });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
    }
  }

  async function handleReplace(asset, file) {
    setBusy(asset.id);
    setUploadProgress(0);
    setStatus({ type: "", message: "" });
    try {
      const updated = await replacePresskitAsset(asset, file, setUploadProgress);
      replaceAssetInState(updated);
      measuredIds.current.delete(asset.id);
      setStatus({ type: "success", message: `${asset.mediaType === "image" ? "Photo" : "Video"} replaced.` });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
      setUploadProgress(null);
    }
  }

  async function handleDelete(asset) {
    if (!window.confirm(`Permanently delete “${asset.altText}”? This cannot be undone.`)) return;
    setBusy(asset.id);
    setStatus({ type: "", message: "" });
    try {
      await deletePresskitAsset(asset);
      setAssets((current) => current.filter((item) => item.id !== asset.id));
      setStatus({ type: "success", message: "Media permanently deleted." });
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
    }
  }

  async function applyOrder(mediaType, orderedGroup) {
    const previous = assets;
    const orderById = new Map(orderedGroup.map((asset, index) => [asset.id, (index + 1) * 10]));
    setAssets((current) => current.map((asset) => orderById.has(asset.id)
      ? { ...asset, displayOrder: orderById.get(asset.id) }
      : asset));
    setBusy(`reorder-${mediaType}`);
    setStatus({ type: "", message: "" });
    try {
      await reorderPresskitAssets(orderedGroup.map((asset) => asset.id));
      setStatus({ type: "success", message: `${mediaType === "image" ? "Photo" : "Video"} order saved.` });
    } catch (error) {
      setAssets(previous);
      setStatus({ type: "error", message: error.message });
    } finally {
      setBusy("");
    }
  }

  function handleMove(asset, direction) {
    const group = [...groupedAssets[asset.mediaType]];
    const fromIndex = group.findIndex((item) => item.id === asset.id);
    const toIndex = fromIndex + direction;
    if (fromIndex < 0 || toIndex < 0 || toIndex >= group.length) return;
    const [moved] = group.splice(fromIndex, 1);
    group.splice(toIndex, 0, moved);
    applyOrder(asset.mediaType, group);
  }

  function handleDrop(target) {
    const group = [...groupedAssets[target.mediaType]];
    const fromIndex = group.findIndex((asset) => asset.id === draggedId);
    const toIndex = group.findIndex((asset) => asset.id === target.id);
    setDraggedId("");
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
    const [moved] = group.splice(fromIndex, 1);
    group.splice(toIndex, 0, moved);
    applyOrder(target.mediaType, group);
  }

  function handleDimensions(asset, dimensions) {
    if (asset.width && asset.height) return;
    if (!dimensions.width || !dimensions.height || measuredIds.current.has(asset.id)) return;
    measuredIds.current.add(asset.id);
    replaceAssetInState({ ...asset, ...dimensions });
    updatePresskitAsset(asset.id, dimensions)
      .then(replaceAssetInState)
      .catch(() => measuredIds.current.delete(asset.id));
  }

  return (
    <section className="admin-media-manager" aria-labelledby="presskit-media-heading">
      <div className="admin-media-heading">
        <div>
          <p className="admin-kicker">Press Kit</p>
          <h2 id="presskit-media-heading">Approved media</h2>
          <p>Drag media to reorder it, or use the Move buttons. Hidden media remains stored but is not shown publicly.</p>
        </div>
        <a href="/presskit" target="_blank" rel="noopener noreferrer">View Press Kit</a>
      </div>

      {status.message && <p className={`admin-message ${status.type}`} role="status" aria-live="polite">{status.message}</p>}
      {uploadProgress !== null && (
        <div className="admin-upload-progress" role="status" aria-live="polite">
          <span>Uploading media: {uploadProgress}%</span>
          <progress value={uploadProgress} max="100">{uploadProgress}%</progress>
        </div>
      )}

      <form className="admin-media-upload" onSubmit={handleUpload}>
        <label>
          Photo or video
          <input name="media" type="file" accept={`${presskitAcceptByType.image},${presskitAcceptByType.video}`} required />
        </label>
        <label>
          Descriptive alt text or video description
          <input name="description" maxLength="500" required />
        </label>
        <button className="admin-primary" disabled={Boolean(busy)}>{busy === "upload" ? "Uploading…" : "Upload and publish"}</button>
        <small>Photos: JPEG, PNG, or WebP up to {formatFileSize(PRESSKIT_LIMITS.image)}. Videos: MP4 or WebM up to {formatFileSize(PRESSKIT_LIMITS.video)}.</small>
      </form>

      {loading ? <p className="admin-loading">Loading approved media…</p> : (
        [
          ["image", "Photos"],
          ["video", "Videos"],
        ].map(([mediaType, heading]) => (
          <section className="admin-media-section" key={mediaType}>
            <h3>{heading} <span>{groupedAssets[mediaType].length}</span></h3>
            {groupedAssets[mediaType].length === 0 ? (
              <p className="admin-media-empty">No approved {heading.toLowerCase()} yet.</p>
            ) : (
              <div className="admin-media-grid">
                {groupedAssets[mediaType].map((asset, index) => (
                  <PresskitMediaCard
                    key={asset.id}
                    asset={asset}
                    index={index}
                    total={groupedAssets[mediaType].length}
                    busy={Boolean(busy)}
                    onDelete={handleDelete}
                    onDescription={handleDescription}
                    onDimensions={handleDimensions}
                    onDragStart={setDraggedId}
                    onDrop={handleDrop}
                    onMove={handleMove}
                    onReplace={handleReplace}
                    onVisibility={handleVisibility}
                  />
                ))}
              </div>
            )}
          </section>
        ))
      )}
    </section>
  );
}
